import { yupResolver } from '@hookform/resolvers/yup'
import { TFunction } from 'i18next'
import { useEffect, useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { ObjectSchema, object, ref } from 'yup'

import { RegisterContract } from '@/Account/application/Register'
import { RegisterOrigin, TokenResponseModel } from '@/Account/domain'
import { RegisterRepository } from '@/Account/domain/RegisterRepository'
import {
  HttpModel,
  LegalDocumentType,
  MetaModel,
  MetaStatusCodes,
  NotificationSeverity,
  browserStorageKeys,
  serverResponseErrors,
} from '@/Shared/domain'
import { legacyLoginSiteUrl } from '@/Shared/infrastructure'
import { useDownloaderHook } from '@/Shared/ui/downloader'
import {
  emailSchemaStep2,
  nonEmptyStringSchema,
  requiredCheckboxSchema,
  stringSchema,
} from '@/Shared/ui/form'
import { useLoader } from '@/Shared/ui/loader'
import { useNotification } from '@/Shared/ui/notifications'
import { openNavigationRoutes } from '@/Shared/ui/routers'
import { formTranslation } from '@/Shared/ui/sharedTranslation'

import { LoginTranslation } from '../accountTranslation'

export interface RegisterForm {
  name: string
  surname1: string
  surname2?: string
  username: string
  email: string
  emailRepeat: string
  password: string
  passwordRepeat: string
  acceptConditions: boolean
}

export interface IBasicformController {
  form: UseFormReturn<RegisterForm>
  showErrror: boolean
  mandatoryFields: boolean
  showErrorPage: boolean
  save: (data: RegisterForm) => Promise<void>
  downloadPublicDocument: (id: LegalDocumentType, name?: string) => Promise<void>
  userNameErrror: boolean
  registerOrigin?: number
  isNursery: boolean
  isServerError: boolean
}

const schema = (t: TFunction<'translation', undefined>): ObjectSchema<RegisterForm> =>
  object({
    name: nonEmptyStringSchema(t, 20).matches(
      /^[^0-9]*$/,
      t(formTranslation.errors.noValidFormat),
    ),
    surname1: nonEmptyStringSchema(t, 20).matches(
      /^[^0-9]*$/,
      t(formTranslation.errors.noValidFormat),
    ),
    surname2: stringSchema(t, 20).matches(
      /^[^0-9]*$/,
      t(formTranslation.errors.noValidFormat),
    ),
    username: nonEmptyStringSchema(t, 50)
      .min(4, t(formTranslation.errors.minChars, { value: 4 }))
      .matches(/^[A-Za-z0-9-.]+$/, t(formTranslation.errors.noValidFormat)),
    email: emailSchemaStep2(t, true).required(),
    emailRepeat: stringSchema(t)
      .oneOf([ref('email')], t(formTranslation.errors.emailMatchStep2))
      .defined(),
    password: nonEmptyStringSchema(t).matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.{8,})/,
      t(formTranslation.errors.passwordFormat),
    ),
    passwordRepeat: stringSchema(t)
      .oneOf([ref('password')], t(formTranslation.errors.passwordsDoNotMatch))
      .defined(),
    acceptConditions: requiredCheckboxSchema(t).required(),
  })

export const useBasicformController = (
  repository: RegisterRepository,
): IBasicformController => {
  const { t } = useTranslation()
  const { downloadPublicDocument } = useDownloaderHook()
  const navigate = useNavigate()
  const { startLoading, stopLoading } = useLoader()
  const { addNotification } = useNotification()

  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const token = queryParams.get('token') ?? ''

  const [showErrror, setShowError] = useState<boolean>(false)
  const [userNameErrror, setUserNameError] = useState<boolean>(false)
  const [showErrorPage, setShowErrorPage] = useState<boolean>(false)
  const [mandatoryFields, setMandatoryFields] = useState<boolean>(false)
  const [registerOrigin, setRegisterOrigin] = useState<RegisterOrigin>(
    RegisterOrigin.unknownOrigin,
  )
  const [isNursery, setIsNursery] = useState<boolean>(false)
  const [isServerError, setIsServerError] = useState<boolean>(false)

  const form = useForm<RegisterForm>({
    resolver: yupResolver(schema(t)),
    mode: 'all',
  })

  const validateMandatoryFields = (): void => {
    const name = form.watch('name').trim() !== ''
    const surname = form.watch('surname1').trim() !== ''
    const username = form.watch('username').trim() !== ''
    const email = form.watch('email').trim() !== ''
    const emailrepeat = form.watch('emailRepeat').trim() !== ''
    const password = form.watch('password').trim() !== ''
    const passwordrepeat = form.watch('passwordRepeat').trim() !== ''
    const privacypolicy = form.watch('acceptConditions') !== false
    const value =
      name &&
      surname &&
      username &&
      email &&
      emailrepeat &&
      password &&
      passwordrepeat &&
      privacypolicy
    setMandatoryFields(value && form.formState.isValid)
  }

  const save = async (data: RegisterForm): Promise<void> => {
    startLoading()
    const response = await RegisterContract(repository).createAccount(data, token)
    stopLoading()

    if (response?.meta.status === MetaStatusCodes.ERROR) {
      handleError(response.meta)
    } else {
      localStorage.setItem(browserStorageKeys.token, response.data?.token ?? '')
      addNotification(
        t(LoginTranslation.register.basicForm.success),
        NotificationSeverity.success,
      )
      navigateToStepThree(response)
    }
  }
  const handleError = (meta: MetaModel) => {
    if (meta.messages[0].code === 'IDAERR0005') {
      setUserNameError(true)
    }
    setShowError(true)
  }

  const navigateToStepThree = (response: HttpModel<TokenResponseModel>): void => {
    if (response.data) {
      if (response.data.isLegacyUser) {
        window.location.replace(legacyLoginSiteUrl)
      } else {
        navigate(openNavigationRoutes.registerStepThree, {
          state: {
            fromRegisterPage: true,
            otpGuid: response.data.otpGuid,
            otpEmail: response.data.otpEmail,
          },
        })
      }
    }
  }

  const validateRegisterToken = async (token: string): Promise<void> => {
    startLoading()
    const response = await RegisterContract(repository).validateToken(token)
    stopLoading()
    const data = response?.data

    if (data && typeof data === 'object') {
      const payload = data as {
        userFirstname?: string
        userSurname1?: string
        userSurname2?: string
        email?: string
        accountCreationFrom?: RegisterOrigin
        isNursery?: boolean
      }

      form.setValue('name', payload.userFirstname ?? '')
      form.setValue('surname1', payload.userSurname1 ?? '')
      form.setValue('surname2', payload.userSurname2 ?? '')
      form.setValue('email', payload.email ?? '')
      setRegisterOrigin(payload.accountCreationFrom ?? RegisterOrigin.unknownOrigin)
      setIsNursery(payload.isNursery ?? false)
    }

    // Detectar si es error 500 específicamente
    if (!response || !response.meta || response?.meta.status === MetaStatusCodes.ERROR) {
      // Verificar si es específicamente un error 500 (SERVER_ERROR)
      const isServerErr =
        response?.meta?.messages?.[0]?.code === serverResponseErrors.serverError ||
        response?.meta?.messages?.[0]?.value === serverResponseErrors.server503
      setIsServerError(isServerErr)
      setShowErrorPage(true)
      setIsNursery(data?.isNursery ?? false)
      return
    }
  }

  useEffect(() => {
    if (!showErrorPage) {
      validateMandatoryFields()
    }
  }, [form.watch()])

  useEffect(() => {
    if (token !== '') {
      validateRegisterToken(token)
    }
  }, [token])

  useEffect(() => {
    if (userNameErrror) {
      form.setError('username', {
        type: 'server',
        message: t(LoginTranslation.register.registerForm.userNameError),
      })
    }
  }, [userNameErrror])

  return {
    form,
    save,
    showErrror,
    mandatoryFields,
    showErrorPage,
    downloadPublicDocument,
    userNameErrror,
    registerOrigin,
    isNursery,
    isServerError,
  }
}
